import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../contexts/AuthContext";
import { usePlan } from "../../contexts/PlanContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import {
    Users,
    UserPlus,
    Mail,
    Shield,
    Clock,
    Loader2,
    MoreVertical,
    Edit,
    Trash2,
    Send,
    X,
    CheckCircle,
    AlertCircle,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const ROLES = [
    { value: "tenant_admin", label: "Administrateur", description: "Accès complet à toutes les données" },
    { value: "project_manager", label: "Chargé de projet", description: "Gère ses propres projets" },
    { value: "collaborator", label: "Collaborateur", description: "Accès aux projets assignés uniquement" },
];

const getRoleColor = (role) => {
    switch (role) {
        case "admin":
        case "tenant_admin":
            return "bg-violet-500";
        case "project_manager":
            return "bg-blue-500";
        case "collaborator":
            return "bg-green-500";
        default:
            return "bg-gray-500";
    }
};

const getRoleLabel = (role) => {
    const roleMap = {
        admin: "Administrateur",
        tenant_admin: "Administrateur",
        project_manager: "Chargé de projet",
        collaborator: "Collaborateur",
        employee: "Employé",
    };
    return roleMap[role] || role;
};

export const UsersPage = () => {
    const { t } = useTranslation();
    const { get, post, put, del } = useApi();
    const { user: currentUser } = useAuth();
    const { canCreate, getUsage, refreshPlan } = usePlan();
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteDialog, setInviteDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [cancelInviteDialog, setCancelInviteDialog] = useState(null);
    const [saving, setSaving] = useState(false);

    const [inviteForm, setInviteForm] = useState({
        email: "",
        name: "",
        role: "collaborator",
    });

    const fetchData = useCallback(async () => {
        const results = await Promise.allSettled([
            get("/users"),
            get("/users/invitations"),
        ]);
        if (results[0].status === "fulfilled") setUsers(results[0].value || []);
        if (results[1].status === "fulfilled") setInvitations(results[1].value || []);
        setLoading(false);
    }, [get]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleInvite = async () => {
        if (!inviteForm.email || !inviteForm.name) {
            toast.error("Veuillez remplir tous les champs");
            return;
        }
        setSaving(true);
        try {
            await post("/users/invite", inviteForm);
            toast.success(`Invitation envoyée à ${inviteForm.email}`);
            setInviteDialog(false);
            setInviteForm({ email: "", name: "", role: "collaborator" });
            fetchData();
        } catch (error) {
            toast.error(error.message || "Erreur lors de l'envoi de l'invitation");
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateUser = async () => {
        if (!editDialog) return;
        setSaving(true);
        try {
            await put(`/users/${editDialog.user_id}`, { role: editDialog.role });
            toast.success("Utilisateur mis à jour");
            setEditDialog(null);
            fetchData();
        } catch (error) {
            toast.error(error.message || "Erreur lors de la mise à jour");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteDialog) return;
        setSaving(true);
        try {
            await del(`/users/${deleteDialog.user_id}`);
            toast.success("Utilisateur supprimé");
            setDeleteDialog(null);
            fetchData();
        } catch (error) {
            toast.error(error.message || "Erreur lors de la suppression");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelInvitation = async () => {
        if (!cancelInviteDialog) return;
        setSaving(true);
        try {
            await del(`/users/invitations/${cancelInviteDialog.invitation_id}`);
            toast.success("Invitation annulée");
            setCancelInviteDialog(null);
            fetchData();
        } catch (error) {
            toast.error(error.message || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const isAdmin = currentUser?.role === "admin" || currentUser?.role === "tenant_admin";

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                        Gestion des utilisateurs
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {users.length} utilisateur{users.length > 1 ? "s" : ""} · {invitations.length} invitation{invitations.length > 1 ? "s" : ""} en attente
                    </p>
                </div>
                {isAdmin && (() => {
                    const userUsage = getUsage("users");
                    const canInvite = canCreate("users");
                    return (
                        <div className="flex items-center gap-3">
                            {userUsage.limit !== -1 && (
                                <span className={`text-xs font-mono ${!canInvite ? "text-red-500 font-semibold" : "text-muted-foreground"}`} data-testid="user-usage-badge">
                                    {userUsage.current}/{userUsage.limit}
                                </span>
                            )}
                            <Button
                                onClick={() => { if (!canInvite) { toast.error("Limite d'utilisateurs atteinte. Veuillez upgrader votre plan."); return; } setInviteDialog(true); }}
                                data-testid="invite-user-btn"
                                disabled={!canInvite}
                            >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Inviter un utilisateur
                            </Button>
                        </div>
                    );
                })()}
            </div>

            {/* Role Legend */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="w-5 h-5 text-violet-500" />
                        Niveaux d'accès
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {ROLES.map((role) => (
                            <div key={role.value} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                                <Badge className={getRoleColor(role.value)}>{role.label}</Badge>
                                <p className="text-sm text-muted-foreground">{role.description}</p>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading">Utilisateurs actifs</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Utilisateur</TableHead>
                                <TableHead>Rôle</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead>Inscrit le</TableHead>
                                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.user_id}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                <span className="text-sm font-medium text-primary">
                                                    {user.name?.charAt(0)?.toUpperCase() || "?"}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="font-medium">{user.name || "Sans nom"}</p>
                                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                                    <Mail className="w-3 h-3" />
                                                    {user.email}
                                                </p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={getRoleColor(user.role)}>
                                            {getRoleLabel(user.role)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {user.is_active !== false ? (
                                            <Badge variant="outline" className="border-green-500 text-green-600">
                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                Actif
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="border-red-500 text-red-600">
                                                <AlertCircle className="w-3 h-3 mr-1" />
                                                Inactif
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {user.created_at ? formatDate(user.created_at) : "-"}
                                    </TableCell>
                                    {isAdmin && (
                                        <TableCell className="text-right">
                                            {user.user_id !== currentUser?.user_id && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => setEditDialog(user)}>
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Modifier le rôle
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={() => setDeleteDialog(user)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" />
                                                            Supprimer
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {users.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        Aucun utilisateur
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Pending Invitations */}
            {invitations.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Clock className="w-5 h-5 text-amber-500" />
                            Invitations en attente
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {invitations.map((inv) => (
                                <div
                                    key={inv.invitation_id}
                                    className="flex items-center justify-between p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
                                >
                                    <div className="flex items-center gap-4">
                                        <Send className="w-5 h-5 text-amber-600" />
                                        <div>
                                            <p className="font-medium">{inv.name}</p>
                                            <p className="text-sm text-muted-foreground">{inv.email}</p>
                                        </div>
                                        <Badge className={getRoleColor(inv.role)}>{getRoleLabel(inv.role)}</Badge>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-muted-foreground">
                                            Expire le {formatDate(inv.expires_at)}
                                        </span>
                                        {isAdmin && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setCancelInviteDialog(inv)}
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Invite Dialog */}
            <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Inviter un utilisateur</DialogTitle>
                        <DialogDescription>
                            L'utilisateur recevra un email avec un lien pour créer son compte.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="invite-name">Nom complet</Label>
                            <Input
                                id="invite-name"
                                value={inviteForm.name}
                                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                                placeholder="Jean Dupont"
                                data-testid="invite-name-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invite-email">Email</Label>
                            <Input
                                id="invite-email"
                                type="email"
                                value={inviteForm.email}
                                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                placeholder="jean.dupont@example.com"
                                data-testid="invite-email-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invite-role">Rôle</Label>
                            <Select
                                value={inviteForm.role}
                                onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                            >
                                <SelectTrigger data-testid="invite-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES.map((role) => (
                                        <SelectItem key={role.value} value={role.value}>
                                            {role.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-sm text-muted-foreground mt-1">
                                {ROLES.find((r) => r.value === inviteForm.role)?.description}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInviteDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleInvite} disabled={saving} data-testid="send-invite-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Envoyer l'invitation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Role Dialog */}
            <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Modifier le rôle</DialogTitle>
                        <DialogDescription>
                            Changer le niveau d'accès de {editDialog?.name}
                        </DialogDescription>
                    </DialogHeader>
                    {editDialog && (
                        <div>
                            <Label>Nouveau rôle</Label>
                            <Select
                                value={editDialog.role}
                                onValueChange={(v) => setEditDialog({ ...editDialog, role: v })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES.map((role) => (
                                        <SelectItem key={role.value} value={role.value}>
                                            {role.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialog(null)}>
                            Annuler
                        </Button>
                        <Button onClick={handleUpdateUser} disabled={saving}>
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer {deleteDialog?.name} ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. L'utilisateur perdra l'accès à tous les projets.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            className="bg-destructive text-destructive-foreground"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Cancel Invitation Confirmation */}
            <AlertDialog open={!!cancelInviteDialog} onOpenChange={() => setCancelInviteDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Annuler l'invitation ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            L'invitation pour {cancelInviteDialog?.email} sera annulée.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Non</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCancelInvitation}>
                            Oui, annuler
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
